import type { BaseDescriptor, UniqueIdentifier } from "@piperunner-cms/types/v1";
import type { DocumentSnapshot } from "firebase/firestore";
import { isArray, isEmpty, isUndefined, omitBy } from "lodash";
import isNil from "lodash/isNil";
import mapValues from "lodash/mapValues";

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export const isUniqueIdentifierPresent = (userId: any, projectId: any) => {
  if (!userId || !projectId) {
    throw new Error("Both userId and projectId are required");
  }
  return true;
};

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export const isDocumentIdentifierPresent = (documentId: any) => {
  if (!documentId) {
    throw new Error("Document identifier is required");
  }
  return true;
};

export const doesDocExist = (documentSnapshot: DocumentSnapshot) => {
  if (!documentSnapshot.exists()) {
    throw new Error("Document does not exist");
  }
  return true;
};

export const doesDocumentBelongToUser = (uniqueIdentifier: UniqueIdentifier, documentSnapshot: DocumentSnapshot) => {
  if (documentSnapshot.exists()) {
    const data = documentSnapshot.data();
    if (data.userId !== uniqueIdentifier.userId || data.projectId !== uniqueIdentifier.projectId) {
      throw new Error("Document does not belong to the specified user and project");
    }
    return true;
  }
};

/**
 * Normalizes an object by replacing undefined, null, or empty string values with a specified replacement value.
 *
 * @param obj - The object to normalize.
 * @param replaceWith - The value to replace undefined, null, or empty string values with. Defaults to `null`.
 *
 * @returns {object} A new object with normalized values.
 */
export const normalizeObject = (
  obj: object | null | undefined,
  options: {
    replaceWith: "" | null;
    dropKeys: boolean;
  } = {
    replaceWith: null,
    dropKeys: false,
  },
): object => {
  if (typeof obj !== "object" || obj === null || obj === undefined) {
    console.warn("normalizeObject: Expected an object, null, or undefined but received:", obj);
    return obj;
  }

  if (isArray(obj)) {
    if (isEmpty(obj)) {
      return null;
    }

    return obj.map((item) => {
      return normalizeObject(item, options);
    });
  }

  if (
    options.dropKeys &&
    Object.values(obj).every((val) => isNil(val) || val === "" || (isArray(val) && isEmpty(val)))
  ) {
    omitBy(obj, isUndefined);
  }

  return mapValues(obj, (val) => {
    if (isNil(val) || val === "" || val === false) {
      const replacement = options.replaceWith === "" ? "" : null;
      return replacement;
    }

    if (isArray(val)) {
      // @ts-expect-error
      return val.map((item) => normalizeObject(item, options.replaceWith));
    }

    return val;
  });
};

/**
 * Utility to filter out dead descriptors from a list of options.
 * A dead descriptor is one that does not exist in the options list.
 * This is useful when the value is stored as an ID, and the options list
 * may have changed since the value was set.
 * @param value The current value or list of values (IDs)
 * @param options The list of valid options
 * @returns The filtered value or list of values (IDs)
 */
export function filterDeadDescriptors<T extends BaseDescriptor, G extends T["id"] | T["id"][] | null>(
  value: G,
  options: T[],
): G | null {
  if (!value) return value;

  if (Array.isArray(value)) {
    return value.filter((v) => options.some(({ id }) => id === v)) as G;
  }

  return options.some(({ id }) => id === value) ? value : null;
}
